import React, { useEffect, useRef, useState } from 'react';
import {
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
import {fontQuicksand} from '../mixin';
import Styled from 'styled-components';
import { handlePrepaidCardRejection, PREPAID_CARD_ERROR_MSG } from '../../../utils/web-helper';
// [AI GENERATED CODE] - Import Store to get current_user
import {Store} from '../../../stores/parent-dashboard';

const SetupForm = ({
  elementOptions,
  savedCardsContainer,
  savedCardsPaymentHandler,
  savedPaymentMethodsContainer,
  savedPaymentMethodsHandler,
  showPaymentElement,
  selectedCardId,
  selectedPaymentMethodId,
  paymentElementOptions,
  purchaseCta,
  stripeSuccessCallback,
  stripeErrorCallback,
  clientSecret,
  emailField,
  emailValidationCallback,
  customContainerA,
  customContainerB,
  deviceLayoutManager,
  paymentElementOnReadyCallback,
  paymentElementOnChangeCallback,
  error
}) => {
  const stripe = useStripe();
  const elements = useElements();
  if (!stripe || !elements) return null;

  const [email, setEmail] = useState('');
  const [emailErrorMessage,setEmailErrorMessage] = useState('');
  const [paymentElementLoaderStarted, setPaymentElementLoaderStarted] = useState(false)
  const [paymentElementReady, setPaymentElementReady] = useState(false);
  const paymentElementFocussedRef = useRef(false);
  const [lastOnChangeEvent, setLastOnChangeEvent] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(()=>{
    if (!showPaymentElement){
      setPaymentElementLoaderStarted(false)
      setPaymentElementReady(false)
      paymentElementFocussedRef.current = false;
      setLastOnChangeEvent(null)
    }
  }, [showPaymentElement])

  useEffect(() => {
    setErrorMessage(error);
  }, [error])

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage(null);
    if (savedCardsContainer && selectedCardId) {
      savedCardsPaymentHandler(setErrorMessage)
      return;
    }

    if (savedPaymentMethodsContainer && selectedPaymentMethodId) {
      savedPaymentMethodsHandler(setErrorMessage)
      return;
    }

    if (!stripe || !elements) {
      return;
    }

    if (emailField && emailValidationCallback) {
      if (!emailValidationCallback(email, setEmailErrorMessage)) {
        return;
      }
    }

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: getReturnUrl()
      },
      redirect: "if_required"
    });
    if (error) {
      // Check if this is a prepaid card from the error response
      // [AI GENERATED CODE] - Get currentUser from Store and pass to handlePrepaidCardRejection
      const { current_user } = Store.getState();
      if (handlePrepaidCardRejection({
        cardData: error.payment_method,
        setErrorFunc: setErrorMessage,
        getRenderedElementFunc: () => 'Payment Element',
        currentUser: current_user
      })) {
        // Already handled by the utility function
      } else {
        errorCallback(error);
      }
    } else {
      await successCallback(setupIntent);
    }
  };

  const getReturnUrl = () => {
    return window.location.origin + window.location.pathname + '#purchase';
  }

  const successCallback = async (setupIntent) => {
    try{
      switch (setupIntent.status) {
        case 'succeeded':
          await stripeSuccessCallback(setupIntent, setErrorMessage, email, {
            lastOnChangeEvent
          })
          break;

        default:
          console.error("Error Status: received" + setupIntent.status);
          errorCallback({message: "Something Went Wrong"},setupIntent);
          break;
      }
    }catch(e){
      console.error(e);
      errorCallback({message: "Something Went Wrong"},setupIntent);
    }
  }

  const errorCallback = (error,setupIntent) => {
    if (stripeErrorCallback) {
      stripeErrorCallback(error,setupIntent, setErrorMessage);
    }
  }


  const baseComponent = (
    <form>
      {savedCardsContainer && savedCardsContainer({setPaymentElementError: setErrorMessage})}
      {savedPaymentMethodsContainer && savedPaymentMethodsContainer({setPaymentElementError: setErrorMessage})}
      {showPaymentElement && (
        <>
          {emailField && paymentElementReady && emailField({ email, setEmail, emailErrorMessage,setEmailErrorMessage })}
          <PaymentElementWrapper>
            <PaymentElement
              options={paymentElementOptions}
              onFocus={(e) => {
                paymentElementFocussedRef.current = true 
              }}
              onChange={(e) => {
                paymentElementOnChangeCallback && paymentElementOnChangeCallback({
                  paymentElementReady,
                  paymentElementFocussedRef,
                  lastEvent: lastOnChangeEvent,
                  currentEvent: e, 
                  setErrorMessage, 
                  setEmailErrorMessage
                })
                setLastOnChangeEvent(e);
                
                // Don't reset error if it's a prepaid card error
                const errorElement = document.getElementById('payment-error-element');
                const errorText = errorElement?.textContent || '';
                if (errorText === PREPAID_CARD_ERROR_MSG) {
                  return;
                }
              }}
              onBlur={(e) => {
                paymentElementFocussedRef.current = false
              }}
              onLoaderStart = {(e) => {
                setPaymentElementLoaderStarted(true)
              }}
              onLoadError = {(e) => {
                console.error("OnLoaderErrorPaymentElementEvent",e)
              }}
              onReady={(e) => {
                setPaymentElementReady(true);
                paymentElementOnReadyCallback && paymentElementOnReadyCallback(e)
              }}
            />
          </PaymentElementWrapper>
        </>
      )}
      {errorMessage && <ErrorWrapper>{errorMessage} </ErrorWrapper>}
      {showPaymentElement && (paymentElementLoaderStarted || paymentElementReady) && customContainerA && customContainerA()}
      {purchaseCta &&
        purchaseCta({ clickHandler: handleSubmit, disableBtn: !stripe, paymentElementProps: {lastOnChangeEvent} })}
      {showPaymentElement && (paymentElementLoaderStarted || paymentElementReady) && customContainerB && customContainerB()}
      {/* <PaymentCta clickHandler={handleSubmit} fromCreditForm={true} settings={settings} disableBtn={!stripe}/> */}
      {/* Show error message to your customers */}
    </form>
  );

  let componentToRender = baseComponent;
  if (deviceLayoutManager) {
    componentToRender = deviceLayoutManager(componentToRender, {
      elements,
      appearance: elementOptions?.appearance || {},
    });
  }

  return <>{componentToRender}</>;
};

const PaymentElementWrapper = Styled.div`
  margin-top: 1.8rem;
`;

export const ErrorWrapper = Styled.div`
  color: #EB6D45;
  ${fontQuicksand(1.2, 1.6, 5)};
  min-height: 1.7rem;
`;

export default SetupForm;
